import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Assignment } from '@mui/icons-material';

import { RequestMaterialItemOption, RequestMaterialLine, RequestMaterialUomOption } from '../types';

interface AssignItem {
  lineId: number;
  itemCode: string;
  itemName: string;
  requestedQty: number;
  assignQty: number;
  uomId: string;
  uomName: string;
  availableQty: number | undefined;
}

export interface AssignMaterialDraft {
  orderId: string;
  orderNo: string;
  items: AssignItem[];
}

interface AssignMaterialToOrderModalProps {
  open: boolean;
  onClose: () => void;
  selectedLines: RequestMaterialLine[];
  itemOptions: RequestMaterialItemOption[];
  uomOptions: RequestMaterialUomOption[];
  orderId?: string;
  orderNo?: string;
  orderName?: string;
  availableQtyByLineId?: Record<number, number>;
  onConfirm: (draft: AssignMaterialDraft) => void;
}

const formatQty = (value: number) =>
  value.toLocaleString('vi-VN', { maximumFractionDigits: 2 });

const canAssign = (availableQty: number | undefined): boolean =>
  availableQty !== undefined && availableQty > 0;

export default function AssignMaterialToOrderModal({
  open,
  onClose,
  selectedLines,
  itemOptions,
  uomOptions,
  orderId = '',
  orderNo = '',
  orderName = '',
  availableQtyByLineId = {},
  onConfirm,
}: AssignMaterialToOrderModalProps) {
  const [items, setItems] = React.useState<AssignItem[]>([]);

  React.useEffect(() => {
    if (!open) return;

    const itemLines = selectedLines.filter((line) => line.line_type === 'item' && line.item_id);
    setItems(
      itemLines.map((line) => {
        const matchedItem = itemOptions.find((item) => item.id === line.item_id);
        const uom = uomOptions.find((u) => u.id === line.uom_id);
        const requestedQty = Number(line.requested_qty) || 0;
        const available = availableQtyByLineId[line.id];
        const assignQty = canAssign(available)
          ? Math.min(requestedQty, available as number)
          : 0;
        return {
          lineId: line.id,
          itemCode: line.item_code || matchedItem?.code || '',
          itemName: line.item_name || matchedItem?.name || '',
          requestedQty,
          assignQty,
          uomId: line.uom_id || '',
          uomName: uom?.name || line.uom_name || '',
          availableQty: available,
        };
      }),
    );
  }, [open, selectedLines, itemOptions, uomOptions, availableQtyByLineId]);

  const handleQtyChange = (index: number, value: string) => {
    const raw = parseFloat(value);
    setItems((prev) => {
      const next = [...prev];
      const item = { ...next[index] };
      const available = item.availableQty as number;
      item.assignQty = isNaN(raw)
        ? item.assignQty
        : Math.min(Math.max(0.01, Math.round(raw * 100) / 100), available);
      next[index] = item;
      return next;
    });
  };

  const assignableItems = items.filter((item) => canAssign(item.availableQty));
  const blockedCount = items.length - assignableItems.length;
  const hasOrder = Boolean(orderId);

  return (
    <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Assignment sx={{ color: '#7c3aed', fontSize: '1.25rem' }} />
          <Typography variant='h6' sx={{ fontWeight: 700, color: '#111827' }}>
            Gán vật liệu cho đơn hàng
          </Typography>
        </Box>
        <Typography sx={{ fontSize: '0.82rem', color: '#6b7280', mt: 0.5 }}>
          {assignableItems.length} / {items.length} vật tư đủ điều kiện gán
        </Typography>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 3 }}>
        {/* Order info banner */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            mb: 2.5,
            p: 2,
            backgroundColor: hasOrder ? '#f5f3ff' : '#fef3c7',
            border: `1px solid ${hasOrder ? '#ddd6fe' : '#fde68a'}`,
            borderRadius: 1,
          }}
        >
          <Assignment sx={{ color: hasOrder ? '#7c3aed' : '#d97706', fontSize: '1.2rem', flexShrink: 0 }} />
          {hasOrder ? (
            <Box>
              <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#7c3aed', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Đơn hàng
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mt: 0.25 }}>
                <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: '#1e1b4b', fontFamily: '"Roboto Mono", monospace' }}>
                  {orderNo}
                </Typography>
                {orderName && (
                  <Typography sx={{ fontSize: '0.85rem', color: '#6b7280' }}>{orderName}</Typography>
                )}
              </Box>
            </Box>
          ) : (
            <Typography sx={{ fontSize: '0.875rem', color: '#92400e' }}>
              Chưa chọn đơn hàng. Vui lòng chọn đơn hàng trước khi gán vật liệu.
            </Typography>
          )}
        </Box>

        {/* Warning banner when some items are blocked */}
        {blockedCount > 0 && (
          <Alert severity='warning' sx={{ mb: 2.5, fontSize: '0.82rem' }}>
            <strong>{blockedCount} vật tư</strong> không đủ tồn khả dụng (tồn kho &minus; đã gán &le; 0) và sẽ không được gán.
          </Alert>
        )}

        {/* Items table */}
        <Box sx={{ border: '1px solid #e5e7eb', borderRadius: 1, overflow: 'hidden' }}>
          <Table size='small'>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f9fafb' }}>
                <TableCell sx={{ fontWeight: 600, color: '#374151', width: '40px' }}>#</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#374151', width: '130px' }}>Mã vật tư</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Tên vật tư</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#374151', width: '150px' }}>
                  Số lượng gán
                  <Typography component='span' sx={{ fontSize: '0.7rem', color: '#9ca3af', fontWeight: 400, ml: 0.5 }}>
                    (YC)
                  </Typography>
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#374151', width: '110px' }}>ĐVT</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#374151', width: '140px' }}>Tồn khả dụng</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item, index) => {
                const eligible = canAssign(item.availableQty);
                const available = item.availableQty;

                return (
                  <TableRow
                    key={item.lineId}
                    sx={{
                      '&:last-child td': { borderBottom: 0 },
                      backgroundColor: eligible ? 'inherit' : '#fafafa',
                      opacity: eligible ? 1 : 0.6,
                    }}
                  >
                    <TableCell sx={{ color: '#6b7280', fontSize: '0.8rem' }}>{index + 1}</TableCell>
                    <TableCell>
                      <Typography
                        sx={{
                          fontSize: '0.875rem',
                          fontWeight: 600,
                          color: eligible ? '#374151' : '#9ca3af',
                          fontFamily: '"Roboto Mono", monospace',
                        }}
                      >
                        {item.itemCode || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: '0.875rem', color: eligible ? '#374151' : '#9ca3af' }}>
                        {item.itemName || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {eligible ? (
                        <TextField
                          size='small'
                          type='number'
                          value={item.assignQty}
                          onChange={(e) => handleQtyChange(index, e.target.value)}
                          slotProps={{
                            htmlInput: { min: 0.01, max: available, step: 0.01 },
                            formHelperText: { sx: { fontSize: '0.68rem', mx: 0, whiteSpace: 'nowrap' } },
                          }}
                          helperText={`YC: ${item.requestedQty} | Tối đa: ${formatQty(available as number)}`}
                          sx={{ width: '120px', '& .MuiInputBase-input': { fontSize: '0.875rem' } }}
                        />
                      ) : (
                        <Typography sx={{ fontSize: '0.82rem', color: '#9ca3af' }}>—</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: '0.875rem', color: eligible ? '#6b7280' : '#9ca3af' }}>
                        {item.uomName || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {available === undefined ? (
                        <Chip
                          label='Chưa tải tồn'
                          size='small'
                          sx={{ fontSize: '0.7rem', backgroundColor: '#f3f4f6', color: '#6b7280', height: 20 }}
                        />
                      ) : available <= 0 ? (
                        <Chip
                          label='Không đủ tồn'
                          size='small'
                          sx={{ fontSize: '0.7rem', backgroundColor: '#fee2e2', color: '#dc2626', height: 20 }}
                        />
                      ) : (
                        <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#059669' }}>
                          {formatQty(available)}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
              {items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} sx={{ textAlign: 'center', py: 3, color: '#9ca3af' }}>
                    Không có vật tư nào được chọn.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button variant='outlined' onClick={onClose} sx={{ textTransform: 'none' }}>
          Hủy
        </Button>
        <Button
          variant='contained'
          disabled={assignableItems.length === 0 || !hasOrder}
          onClick={() => onConfirm({ orderId, orderNo, items: assignableItems })}
          sx={{ textTransform: 'none', backgroundColor: '#7c3aed', '&:hover': { backgroundColor: '#6d28d9' } }}
        >
          Xác nhận gán vật liệu ({assignableItems.length})
        </Button>
      </DialogActions>
    </Dialog>
  );
}
